import { PhoneNumberInput } from "@/components/shared/PhoneNumberInput";
import { QuestionnaireFlowQuestion, QuestionnaireFlowQuestionOption } from "@/types/question.types";
import { NativeSelect, Stack, Textarea } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";
import { BirthdayInput } from "../shared/BirthdayInput";
import { PasswordConfirmationInput } from "../shared/PasswordConfirmationInput";
import { AnswerOptionsList } from "./AnswerOptionsList";
import { RangeButtonOptions } from "./RangeButtonOptions";
import { TextQuestionInput } from "./TextQuestionInput";
import { analyzeQuestion } from "./questionAnalyzer";

export interface BaseQuestionnaireFormValues {
  personal_info?: {
    // Define fields that are directly accessed, e.g., nationality, birthday
    // And allow for other string-indexed personal info fields
    [key: string]: any;
    nationality?: string;
    birthday?: string;
    phoneNumber?: string;
  };
  answers?: {
    [questionId: number]: {
      answerText?: string;
      answerScale?: number;
      answerOptions?: number[];
      answerDate?: string;
    };
  };
}

type QuestionAndAnswerResolverProps<TFormValues> = {
  currentQuestion: QuestionnaireFlowQuestion;
  form: UseFormReturnType<TFormValues>;
  handleSelectMultiple: (option: QuestionnaireFlowQuestionOption) => void;
  handleSelectSingle: (option: QuestionnaireFlowQuestionOption) => void;
  handleSelectRange: (scaleValue: number) => void;
  fieldPath: string;
  customInputValue?: string;
  onCustomInputChange?: (value: string) => void;
};

export function QuestionAndAnswerResolver<TFormValues>({
  currentQuestion,
  form,
  handleSelectMultiple,
  handleSelectSingle,
  handleSelectRange,
  fieldPath,
  customInputValue,
  onCustomInputChange,
}: QuestionAndAnswerResolverProps<TFormValues>) {
  const questionAnalysis = analyzeQuestion(currentQuestion);
  const currentQuestionAnswerOptions = currentQuestion?.extra?.options ?? [];
  const inputProps = form.getInputProps(fieldPath as any);

  return (
    <>
      {questionAnalysis.isSelectQuestion && (
        <Stack gap="lg">
          <AnswerOptionsList
            options={currentQuestionAnswerOptions}
            questionType={currentQuestion.type as "select_multiple" | "select_single"}
            selectedOptions={inputProps.value ?? []}
            onSelectMultiple={handleSelectMultiple}
            onSelectSingle={handleSelectSingle}
            customInputValue={customInputValue}
            onCustomInputChange={onCustomInputChange}
          />
        </Stack>
      )}

      {questionAnalysis.isRangeQuestion && (
        <RangeButtonOptions
          minRange={currentQuestion.extra.min}
          maxRange={currentQuestion.extra.max}
          minText={currentQuestion.extra.minText}
          maxText={currentQuestion.extra.maxText}
          selectedRange={inputProps.value}
          onSelectOption={handleSelectRange}
        />
      )}

      {questionAnalysis.isTextQuestion &&
        (() => {
          if (questionAnalysis.isPhoneNumberField) {
            return <PhoneNumberInput {...inputProps} value={inputProps.value ?? ""} />;
          }
          if (questionAnalysis.isPasswordField) {
            return <PasswordConfirmationInput form={form as any} />;
          }
          return (
            <TextQuestionInput
              questionSlug={currentQuestion.slug}
              {...inputProps}
              value={inputProps.value ?? ""}
            />
          );
        })()}

      {questionAnalysis.isSelectFromListQuestion && (
        <Stack gap="xs" w="100%">
          <NativeSelect
            data={[
              "선택",
              ...(currentQuestion.extra.options ?? []).map((option) => option.displayText),
            ]}
            {...inputProps}
          />
          {(() => {
            const selectedOption = currentQuestion.extra.options?.find(
              (opt) => opt.displayText === inputProps.value
            );
            if (selectedOption?.customInputType === "text_input" && onCustomInputChange) {
              return (
                <Textarea
                  placeholder="입력"
                  value={customInputValue || ""}
                  onChange={(e) => onCustomInputChange(e.currentTarget.value)}
                  styles={{
                    input: {
                      backgroundColor: "#000",
                      color: "#fff",
                    },
                  }}
                />
              );
            }
            return null;
          })()}
        </Stack>
      )}

      {questionAnalysis.isDateQuestion && <BirthdayInput {...inputProps} />}
    </>
  );
}
